import { create } from 'zustand';
import type { EntityUser, FunctionalRole, SessionConfig } from '../types';

const ACTIVE_ROLE_KEY_PREFIX = 'octopm-active-role';

/**
 * Scope a localStorage key to entityId:userId.
 *
 * The key is deliberately NOT global. A shared browser profile with two
 * cofounders on two different tenants (or two different accounts) must
 * never have one's per-account state leak into the other's session — that
 * cross-tenant bleed was the root cause of the reported activeRole defect,
 * and applies equally to any other per-account localStorage flag (see
 * OAuthCallback.tsx / WelcomeModal.tsx's onboarding-return flag). Returns
 * null when either half of the scope isn't known yet (nothing to read/write
 * against).
 */
export function scopedStorageKey(
  prefix: string,
  entityId: string | undefined,
  userId: string | undefined
): string | null {
  if (!entityId || !userId) return null;
  return `${prefix}:${entityId}:${userId}`;
}

function activeRoleStorageKey(
  entityId: string | undefined,
  userId: string | undefined
): string | null {
  return scopedStorageKey(ACTIVE_ROLE_KEY_PREFIX, entityId, userId);
}

function readStoredActiveRole(
  entityId: string | undefined,
  userId: string | undefined
): FunctionalRole | null {
  const key = activeRoleStorageKey(entityId, userId);
  if (!key) return null;
  try {
    return (localStorage.getItem(key) as FunctionalRole | null) ?? null;
  } catch {
    return null;
  }
}

interface UserState {
  currentUser: EntityUser | null;
  sessionConfig: SessionConfig | null;
  /**
   * The role VIEW the user is currently looking at — an ephemeral UI mode,
   * distinct from the account's persistent `primaryRole`. Null means no
   * explicit selection has been made for this entity/user scope; callers
   * should fall back through primaryRole / functionalRoles (see
   * useEffectiveRole()) rather than treating null as "no role".
   */
  activeRole: FunctionalRole | null;
  setUser: (user: EntityUser | null) => void;
  setSessionConfig: (config: SessionConfig | null) => void;
  setActiveRole: (role: FunctionalRole) => void;
  logout: () => void;
}

export const useUserStore = create<UserState>((set, get) => ({
  currentUser: null,
  sessionConfig: null,
  activeRole: null,
  setUser: (user) => {
    // primaryRole is a persistent ACCOUNT property (set deliberately via
    // Settings), not a view mode — it must never overwrite activeRole here.
    // setUser fires on every auth-state change (including silent token
    // refreshes), so unconditionally syncing activeRole from it, as this
    // store used to, wiped out the user's in-session persona choice on a
    // timer unrelated to anything the user did. activeRole is instead
    // (re)seeded only in setSessionConfig, and only when the entity/user
    // scope actually changes.
    set({ currentUser: user });
  },
  setSessionConfig: (config) => {
    const prevEntityId = get().sessionConfig?.entityId;
    const prevUserId = get().sessionConfig?.userId;
    set({ sessionConfig: config });

    if (!config) {
      set({ activeRole: null });
      return;
    }

    // Re-derive activeRole only when the entity/user identity actually
    // changed (first load, login, or switching accounts/tenants in the same
    // browser) — never on a same-identity token refresh, and always scoped
    // to THIS entity+user so another tenant's or account's stored choice
    // can never be read here.
    if (prevEntityId !== config.entityId || prevUserId !== config.userId) {
      set({ activeRole: readStoredActiveRole(config.entityId, config.userId) });
    }
  },
  setActiveRole: (role) => {
    const { sessionConfig } = get();
    const key = activeRoleStorageKey(sessionConfig?.entityId, sessionConfig?.userId);
    if (key) {
      try {
        localStorage.setItem(key, role);
      } catch {
        // ignore storage errors
      }
    }
    set({ activeRole: role });
  },
  logout: () => {
    // Deliberately does not clear the scoped localStorage entry: it is safe
    // to keep (it can only ever be read back for this same entity+user) and
    // doing so preserves the user's persona choice across sign-out/sign-in.
    set({ currentUser: null, sessionConfig: null, activeRole: null });
  },
}));
